import { oc } from "@orpc/contract";
import { z } from "zod";
import {
  APInvoiceGLAccountSummarySchema,
  APInvoiceSupplierSummarySchema,
} from "../accounts-payable/ap-invoices.contract";

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

const InvoicePreviewSchema = z.object({
  id: UUIDSchema,
  invoiceNumber: z.string().nullable().optional(),
  type: z.string().optional(),
  supplierId: UUIDSchema.optional(),
  supplier: APInvoiceSupplierSummarySchema.nullable().optional(),
  glAccount: APInvoiceGLAccountSummarySchema.nullable().optional(),
});

const PaymentInvoicePreviewSchema = InvoicePreviewSchema.extend({
  /** Amount allocated to this payment from the fi_payment_invoices join row. */
  amount: DecimalStringSchema,
  /** Invoice total — for variance vs. allocated amount. */
  totalAmount: DecimalStringSchema.optional(),
  /** Sum of prior allocations already applied to this invoice. */
  paidAmount: DecimalStringSchema.nullable().optional(),
  dueDate: z.string().nullable().optional(),
  paymentStatus: z.string().optional(),
  currency: z.string().optional(),
});

const FinancialTransactionPreviewSchema = z.object({
  id: UUIDSchema,
  transactionDate: z.string(),
  amount: DecimalStringSchema,
  direction: z.string(),
  merchantName: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});

const PaymentExternalActionsSchema = z.object({
  canPreview: z.boolean(),
  canVoid: z.boolean(),
  /**
   * Whether the payment can be (re-)executed. True for payments that are
   * `approved` (stuck — execution never completed) or `failed`, and that have
   * not yet been submitted to the provider (no referenceNumber).
   */
  canRetry: z.boolean(),
});

const PaymentExternalDetailsSchema = z.object({
  provider: z.string(),
  externalPaymentId: z.string().nullable().optional(),
  status: z.string().nullable().optional(),
  imageUri: z.string().nullable().optional(),
  cancellable: z.boolean().nullable().optional(),
  eta: z.string().nullable().optional(),
  sandboxMode: z.boolean(),
  sandboxNextStatuses: z.array(z.string()).optional(),
  remittanceAdvice: z.array(z.array(z.string())).optional(),
  providerError: z.string().nullable().optional(),
  voidBlockedReason: z.string().nullable().optional(),
  lastExecutionAttemptAt: z.coerce.date().nullable().optional(),
  lastExecutionError: z.string().nullable().optional(),
  lastExecutionErrorCode: z.string().nullable().optional(),
  allowedActions: PaymentExternalActionsSchema,
});

export const PAYMENT_PROCESSOR_STATUSES = [
  "PAID",
  "IN_PROCESS",
  "UNPAID",
  "VOID",
  "EXPIRED",
  "PRINTED",
  "MAILED",
  "FAILED",
  "REFUNDED",
] as const;

/** Checkbook payment-processor statuses (the filterable `processorStatus`). */
export const PaymentProcessorStatusSchema = z.enum(PAYMENT_PROCESSOR_STATUSES);

export const PaymentSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  financialTransactionId: UUIDSchema.nullable().optional(),
  /**
   * Supplier bank (SupplierPaymentDetails) the payment will be sent to.
   * Null means the supplier's default details are auto-resolved at approval.
   */
  supplierPaymentDetailsId: UUIDSchema.nullable().optional(),
  paymentAccountId: z.string().nullable().optional(),
  amount: DecimalStringSchema,
  currency: z.string(),
  paymentDate: z.string(),
  method: z.string(),
  status: z.string(),
  hasApprovalRequest: z.boolean().optional(),
  approvalRequestEmailIds: z.array(UUIDSchema).optional(),
  referenceNumber: z.string().nullable().optional(),
  memo: z.string().nullable().optional(),
  /**
   * Whether the payment can be recorded as paid out-of-band (e.g. a manual
   * bank ACH transfer made outside the provider). True for `failed`,
   * `in_review`, and `pending` payments. Lives at the top level (not under
   * externalPayment) so it is available for payments with no provider details.
   */
  canRecordExternal: z.boolean().optional(),
  executionFundingAccountMask: z.string().nullable().optional(),
  executionFundingInstitutionName: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  invoices: z.array(PaymentInvoicePreviewSchema).optional(),
  financialTransaction: FinancialTransactionPreviewSchema.nullable().optional(),
  externalPayment: PaymentExternalDetailsSchema.nullable().optional(),
  /**
   * The payment processor's status (e.g. Checkbook "PAID", "IN_PROCESS",
   * "FAILED"). Surfaced at the top level so list views can show it without the
   * full `externalPayment` block, which requires a per-payment provider call.
   * Null when the payment has no provider reference or the status is unknown.
   */
  processorStatus: z.string().nullable().optional(),
});

export const GetPaymentInputSchema = z.object({
  id: UUIDSchema,
  includeProviderDetails: z.boolean().optional(),
});

export const CreatePaymentInputSchema = z.object({
  amount: DecimalStringSchema,
  currency: z.string().optional(),
  paymentDate: z.string(),
  method: z.string(),
  supplierPaymentDetailsId: UUIDSchema.optional(),
  paymentAccountId: z.string().optional(),
  /**
   * Optional invoice to link. On create the server writes this as a
   * PaymentInvoice junction row (the legacy Payment.invoiceId column was
   * dropped — see migration 20260421180000).
   */
  invoiceId: UUIDSchema.optional(),
  financialTransactionId: UUIDSchema.optional(),
  referenceNumber: z.string().optional(),
  memo: z.string().optional(),
});

export const ListPaymentsInputSchema = z.object({
  /** Filter to payments allocated to this invoice (via fi_payment_invoices). */
  invoiceId: UUIDSchema.optional(),
  status: z.string().optional(),
  excludeStatuses: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  payRunDate: z.string().optional(),
  approvalRequestId: UUIDSchema.optional(),
  /** Only payments to these suppliers (via allocated invoices or bank details). */
  supplierIds: z.array(UUIDSchema).optional(),
  /**
   * Case-insensitive search over invoice numbers, matched PO/SO/TO numbers,
   * payment reference/memo, supplier name and (when numeric) amount.
   */
  search: z.string().optional(),
  /**
   * Only payments whose processor (Checkbook) status is one of these. The
   * status is read live from the provider, not stored.
   */
  // fallow-ignore-next-line code-duplication
  processorStatuses: z.array(PaymentProcessorStatusSchema).optional(),
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

export const ListPaymentsResponseSchema = z.object({
  payments: z.array(PaymentSchema),
  total: z.int().nonnegative(),
  totalAmount: DecimalStringSchema,
});

export const UpdatePaymentInputSchema = z.object({
  id: UUIDSchema,
  data: z
    .object({
      amount: DecimalStringSchema.optional(),
      currency: z.string().optional(),
      paymentDate: z.string().optional(),
      method: z.string().optional(),
      status: z.string().optional(),
      financialTransactionId: UUIDSchema.optional(),
      referenceNumber: z.string().optional(),
      memo: z.string().optional(),
      paymentAccountId: z.string().nullable().optional(),
      /** Null reverts to auto-resolving the supplier default at approval. */
      supplierPaymentDetailsId: UUIDSchema.nullable().optional(),
    })
    .refine(
      (data) => Object.keys(data).length > 0,
      "At least one field must be provided",
    ),
});

export const UpdatePaymentResponseSchema = z.object({
  payment: PaymentSchema.nullable(),
  combinedPaymentIds: z.array(UUIDSchema).optional(),
});

export const ApprovePaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const ApprovePaymentResponseSchema = PaymentSchema;

const ApprovePaymentsByPayRunSchema = z.object({
  type: z.literal("payRun"),
  payRunDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Must be in YYYY-MM-DD format",
  }),
  excludedPaymentIds: z.array(UUIDSchema).optional(),
  scheduledExecutionAt: z.string().datetime().optional(),
  overrideReviewCheck: z.boolean().optional().default(false),
});

const ApprovePaymentsByIdsSchema = z.object({
  type: z.literal("paymentIds"),
  paymentIds: z.array(UUIDSchema).min(1),
  scheduledExecutionAt: z.string().datetime().optional(),
  overrideReviewCheck: z.boolean().optional().default(false),
});

export const ApprovePaymentsInputSchema = z.discriminatedUnion("type", [
  ApprovePaymentsByPayRunSchema,
  ApprovePaymentsByIdsSchema,
]);

export const PaymentDiscrepancySchema = z.object({
  code: z.enum([
    "amount_mismatch",
    "no_invoices_linked",
    "invoice_overpay",
    "invoice_underpay",
    "payment_not_found",
  ]),
  severity: z.enum(["fatal", "blocker", "warning"]),
  message: z.string(),
  invoiceId: UUIDSchema.optional(),
  expectedAmount: DecimalStringSchema.optional(),
  actualAmount: DecimalStringSchema.optional(),
});

export const PaymentReviewResultSchema = z.object({
  paymentId: UUIDSchema,
  paymentAmount: DecimalStringSchema,
  allocatedAmount: DecimalStringSchema,
  discrepancies: z.array(PaymentDiscrepancySchema),
});

export const ReviewPaymentsInputSchema = z.object({
  paymentIds: z.array(UUIDSchema).min(1),
});

export const ReviewPaymentsResponseSchema = z.object({
  results: z.array(PaymentReviewResultSchema),
});

export const ApprovePaymentsResponseSchema = z.object({
  requestedCount: z.int().nonnegative(),
  approvedCount: z.int().nonnegative(),
  approvedPaymentIds: z.array(UUIDSchema),
  executedCount: z.int().nonnegative().optional(),
  executedPaymentIds: z.array(UUIDSchema).optional(),
  scheduledCount: z.int().nonnegative().optional(),
  scheduledPaymentIds: z.array(UUIDSchema).optional(),
  scheduledJobIds: z.array(UUIDSchema).optional(),
  // Payments that recorded an approval but have not yet met the org's
  // required-approvals threshold — still in_review, no execution yet.
  pendingApprovalCount: z.int().nonnegative().optional(),
  pendingApprovalPaymentIds: z.array(UUIDSchema).optional(),
  failedCount: z.int().nonnegative().optional(),
  failedPaymentIds: z.array(UUIDSchema).optional(),
  errorsByPaymentId: z.record(UUIDSchema, z.string()).optional(),
  reviewBlocked: z.boolean().optional(),
  reviewResults: z.array(PaymentReviewResultSchema).optional(),
});

export const RequestApprovalEmailInputSchema = z.object({
  paymentIds: z.array(UUIDSchema).min(1),
  recipientUserIds: z.array(UUIDSchema).optional(),
});

export const GetApprovalRecipientsInputSchema = z.object({});

export const ApprovalRecipientSchema = z.object({
  userId: UUIDSchema,
  name: z.string(),
  email: z.string(),
});

export const GetApprovalRecipientsResponseSchema = z.object({
  recipients: z.array(ApprovalRecipientSchema),
});

export const ListPaymentApprovalsInputSchema = z.object({
  paymentId: UUIDSchema,
});

export const PaymentApprovalEntrySchema = z.object({
  id: UUIDSchema,
  userId: UUIDSchema,
  userName: z.string().nullable(),
  approvedAt: z.string(),
  comment: z.string().nullable(),
  revokedAt: z.string().nullable(),
  revokedById: UUIDSchema.nullable(),
  revokedByName: z.string().nullable(),
  revocationReason: z.string().nullable(),
});

export const ListPaymentApprovalsResponseSchema = z.object({
  approvals: z.array(PaymentApprovalEntrySchema),
});

export const PreviewApprovalRequestEmailInputSchema = z.object({
  paymentIds: z.array(UUIDSchema).min(1),
});

export const PreviewApprovalRequestEmailResponseSchema = z.object({
  from: z.string(),
  subject: z.string(),
  htmlContent: z.string(),
});

export const RequestApprovalEmailResponseSchema = z.object({
  requestedCount: z.int().nonnegative(),
  recipientCount: z.int().nonnegative(),
  outboundEmailId: UUIDSchema,
});

export const RevokeApprovalRequestInputSchema = z.object({
  paymentId: UUIDSchema,
});

export const RevokeApprovalRequestResponseSchema = z.object({
  outboundEmailId: UUIDSchema,
});

export const BulkRevokeApprovalRequestInputSchema = z.object({
  paymentIds: z.array(UUIDSchema).min(1),
});

export const BulkRevokeApprovalRequestResponseSchema = z.object({
  revokedCount: z.number(),
  emailsSent: z.number(),
});

export const PreviewPaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const PreviewPaymentResponseSchema = z.object({
  provider: z.string(),
  amount: DecimalStringSchema,
  currency: z.string(),
  name: z.string(),
  memo: z.string().nullable().optional(),
  imageBase64: z.string(),
  imageMimeType: z.string(),
});

export const RemoveInvoiceFromPaymentInputSchema = z.object({
  paymentId: UUIDSchema,
  invoiceId: UUIDSchema,
});

export const RescheduleInvoiceInputSchema = z.object({
  paymentId: UUIDSchema,
  invoiceId: UUIDSchema,
  newPaymentDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
    message: "Must be in YYYY-MM-DD format",
  }),
});

export const RescheduleInvoiceResponseSchema = z.object({
  sourcePayment: PaymentSchema,
  targetPayment: PaymentSchema,
});

export const VoidPaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const VoidPaymentResponseSchema = PaymentSchema;

export const ExecutePaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const ExecutePaymentResponseSchema = PaymentSchema;

export const RecordExternalPaymentInputSchema = z.object({
  id: UUIDSchema,
});

export const RecordExternalPaymentResponseSchema = PaymentSchema;

export const SimulatePaymentProviderStatusInputSchema = z.object({
  id: UUIDSchema,
  status: PaymentProcessorStatusSchema,
});

export const SimulatePaymentProviderStatusResponseSchema = PaymentSchema;

export const PreviewRemittanceEmailInputSchema = z.object({
  paymentId: UUIDSchema,
});

export const PreviewRemittanceEmailResponseSchema = z.object({
  from: z.string(),
  to: z.string().nullable(),
  subject: z.string(),
  htmlContent: z.string(),
});

const NormalizedEmailSchema = z.string().trim().toLowerCase().pipe(z.email());

export const SendRemittanceEmailInputSchema = z.object({
  paymentId: UUIDSchema,
  toEmails: z.array(NormalizedEmailSchema).min(1),
  ccEmails: z.array(NormalizedEmailSchema).optional(),
});

export const SendRemittanceEmailResponseSchema = z.object({
  success: z.boolean(),
});

export const ListRemittanceEmailsInputSchema = z.object({
  paymentId: UUIDSchema,
});

// ── AI payment review ────────────────────────────────────────────────

export const RunAiReviewInputSchema = z.object({
  paymentId: UUIDSchema,
  triggerReason: z.string().optional(),
});

export const RunAiReviewResponseSchema = z.object({
  started: z.boolean(),
  alreadyRunning: z.boolean(),
  agentRunId: UUIDSchema.nullable(),
});

export const GetAiReviewInputSchema = z.object({
  paymentId: UUIDSchema,
});

export const AiReviewStatusSchema = z.enum([
  "idle",
  "running",
  "passed",
  "failed",
  "unavailable",
]);

export const AiReviewSummarySchema = z.object({
  status: AiReviewStatusSchema,
  isRunning: z.boolean(),
  reviewPassed: z.boolean().nullable(),
  reasoningSummary: z.string().nullable(),
  issues: z.array(z.string()),
  blockingReasons: z.array(z.string()),
  checksPerformed: z.array(
    z.object({
      category: z.string(),
      status: z.enum(["pass", "fail", "warn", "na"]),
      detail: z.string(),
    }),
  ),
  reviewedAt: z.coerce.date().nullable(),
  agentRunId: UUIDSchema.nullable(),
  error: z.string().nullable(),
});

export const RemittanceEmailSummarySchema = z.object({
  id: UUIDSchema,
  to: z.string(),
  cc: z.string().nullable(),
  subject: z.string(),
  status: z.string().nullable(),
  sentAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
});

export const ListRemittanceEmailsResponseSchema = z.object({
  emails: z.array(RemittanceEmailSummarySchema),
});

export const paymentsContracts = {
  create: oc.input(CreatePaymentInputSchema).output(PaymentSchema).route({
    method: "POST",
    summary: "Create a supplier payment",
    description:
      "Creates an in-review supplier payment with amount, date, method, and optional bank details, memo, and reference. Can link one AP invoice. Does not send money.",
  }),
  get: oc.input(GetPaymentInputSchema).output(PaymentSchema.nullable()).route({
    method: "GET",
    summary: "Get a supplier payment",
    description:
      "Returns one payment with its allocated invoices, supplier, bank transaction, and approval-request state. Optionally includes live processor details such as check status, ETA, and allowed void/retry actions.",
  }),
  list: oc
    .input(ListPaymentsInputSchema)
    .output(ListPaymentsResponseSchema)
    .route({
      method: "GET",
      summary: "List supplier payments",
      description:
        "Lists payments with total count and amount. Filter by invoice, status, date range, pay run date, approval request, suppliers, processor status, or text search over invoice, order, and supplier.",
    }),
  update: oc
    .input(UpdatePaymentInputSchema)
    .output(UpdatePaymentResponseSchema)
    .route({
      method: "PUT",
      summary: "Update a supplier payment",
      description:
        "Edits a payment's amount, date, method, status, bank details, memo, or reference. Cancelling reverses invoice allocations; moving the date merges into the supplier's other in-review payment that day.",
    }),
  approve: oc
    .input(ApprovePaymentInputSchema)
    .output(ApprovePaymentResponseSchema)
    .route({
      method: "POST",
      summary: "Approve and send a supplier payment",
      description:
        "Records the user's approval on an in-review payment. Once required approvals are met, it locks the bank details, sends the payment through the processor, and emails remittance to the supplier.",
    }),
  approveMultiple: oc
    .input(ApprovePaymentsInputSchema)
    .output(ApprovePaymentsResponseSchema)
    .route({
      method: "POST",
      summary: "Approve and send multiple payments",
      description:
        "Approves in-review payments by pay run date or ID list, then sends fully approved ones now or at a scheduled time. Blocks on invoice-amount discrepancies unless overridden.",
    }),
  review: oc
    .input(ReviewPaymentsInputSchema)
    .output(ReviewPaymentsResponseSchema)
    .route({
      method: "POST",
      summary: "Check payments for amount discrepancies",
      description:
        "Checks payments against their linked invoices and returns discrepancies such as amount mismatch, overpay, underpay, or no invoices, each with a severity. Read-only.",
    }),
  requestApprovalEmail: oc
    .input(RequestApprovalEmailInputSchema)
    .output(RequestApprovalEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Email approvers to approve payments",
      description:
        "Sends an approval-request email for the given payments to chosen users, or all users with payment-approval permission. Edits are blocked while the request is active.",
    }),
  revokeApprovalRequest: oc
    .input(RevokeApprovalRequestInputSchema)
    .output(RevokeApprovalRequestResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke a payment approval request",
      description:
        "Withdraws the active approval request on an in-review payment and emails the original recipients a revocation notice, unlocking the payment for edits.",
    }),
  bulkRevokeApprovalRequest: oc
    .input(BulkRevokeApprovalRequestInputSchema)
    .output(BulkRevokeApprovalRequestResponseSchema)
    .route({
      method: "POST",
      summary: "Revoke approval requests for multiple payments",
      description:
        "Withdraws active approval requests on several in-review payments and emails revocation notices to the recipients. Returns how many were revoked and emails sent.",
    }),
  preview: oc
    .input(PreviewPaymentInputSchema)
    .output(PreviewPaymentResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a payment check image",
      description:
        "Validates the payment can be sent and returns a check preview image from the payment processor with payee name, amount, and memo. Does not send money.",
    }),
  void: oc
    .input(VoidPaymentInputSchema)
    .output(VoidPaymentResponseSchema)
    .route({
      method: "POST",
      summary: "Void a supplier payment",
      description:
        "Cancels a sent payment with the processor, reverses its invoice allocations, deletes the linked bank transaction, and marks the payment canceled. Blocked once the check can no longer be cancelled.",
    }),
  execute: oc
    .input(ExecutePaymentInputSchema)
    .output(ExecutePaymentResponseSchema)
    .route({
      method: "POST",
      summary: "Retry sending an approved payment",
      description:
        "Re-sends an approved or failed payment that never reached the processor, refreshing bank details for failed ones, then emails remittance to the supplier.",
    }),
  recordExternalPayment: oc
    .input(RecordExternalPaymentInputSchema)
    .output(RecordExternalPaymentResponseSchema)
    .route({
      method: "POST",
      summary: "Mark a payment as paid outside the system",
      description:
        "Marks a failed, in-review, or pending payment as paid because it was settled outside the system, such as a manual bank transfer, and applies its invoice allocations. Moves no money.",
    }),
  simulateProviderStatus: oc
    .input(SimulatePaymentProviderStatusInputSchema)
    .output(SimulatePaymentProviderStatusResponseSchema)
    .route({
      method: "POST",
      summary: "Simulate a payment processor status",
      description:
        "Sandbox only: triggers a processor status change (e.g. paid, mailed, failed) on a sent payment and returns the updated payment. For testing payment flows.",
    }),
  previewRemittanceEmail: oc
    .input(PreviewRemittanceEmailInputSchema)
    .output(PreviewRemittanceEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a payment remittance email",
      description:
        "Renders the remittance advice email for a payment, listing its paid invoices, with sender, default supplier recipient, subject, and HTML. Does not send.",
    }),
  sendRemittanceEmail: oc
    .input(SendRemittanceEmailInputSchema)
    .output(SendRemittanceEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Send a payment remittance email",
      description:
        "Emails remittance advice for a payment, listing the invoices it pays, to the given supplier addresses with optional CCs.",
    }),
  listRemittanceEmails: oc
    .input(ListRemittanceEmailsInputSchema)
    .output(ListRemittanceEmailsResponseSchema)
    .route({
      method: "GET",
      summary: "List remittance emails for a payment",
      description:
        "Lists remittance advice emails already sent for a payment, with recipients, subject, delivery status, and sent time.",
    }),
  runAiReview: oc
    .input(RunAiReviewInputSchema)
    .output(RunAiReviewResponseSchema)
    .route({
      method: "POST",
      summary: "Run AI review on a payment",
      description:
        "Starts an AI agent review of an unpaid payment that checks invoices, supplier, and bank details. Runs in the background; returns the run ID or the one already running.",
    }),
  getAiReview: oc
    .input(GetAiReviewInputSchema)
    .output(AiReviewSummarySchema)
    .route({
      method: "GET",
      summary: "Get a payment's AI review result",
      description:
        "Returns the latest AI review for a payment: status, pass/fail, summary, issues, blocking reasons, and per-check results.",
    }),
  getApprovalRecipients: oc
    .input(GetApprovalRecipientsInputSchema)
    .output(GetApprovalRecipientsResponseSchema)
    .route({
      method: "GET",
      summary: "List payment approvers",
      description:
        "Lists users who have permission to approve payments, with name and email, for choosing approval-request recipients.",
    }),
  listApprovals: oc
    .input(ListPaymentApprovalsInputSchema)
    .output(ListPaymentApprovalsResponseSchema)
    .route({
      method: "GET",
      summary: "List approvals on a payment",
      description:
        "Lists every approval recorded on a payment, including revoked ones, with approver, time, comment, and revocation details.",
    }),
  previewApprovalRequestEmail: oc
    .input(PreviewApprovalRequestEmailInputSchema)
    .output(PreviewApprovalRequestEmailResponseSchema)
    .route({
      method: "POST",
      summary: "Preview a payment approval request email",
      description:
        "Renders the approval-request email for the given payments, returning sender, subject, and HTML. Does not send.",
    }),
  removeInvoice: oc
    .input(RemoveInvoiceFromPaymentInputSchema)
    .output(PaymentSchema)
    .route({
      method: "POST",
      summary: "Remove an invoice from a payment",
      description:
        "Removes an invoice from an in-review payment and recalculates the payment total. The invoice returns to accepted if no other active payment covers it.",
    }),
  rescheduleInvoice: oc
    .input(RescheduleInvoiceInputSchema)
    .output(RescheduleInvoiceResponseSchema)
    .route({
      method: "POST",
      summary: "Move an invoice to another payment date",
      description:
        "Moves an invoice off an in-review payment onto the supplier's in-review payment for a new date, creating one if needed. Recalculates both totals.",
    }),
} as const;

export type Payment = z.infer<typeof PaymentSchema>;
export type PaymentExternalDetails = z.infer<
  typeof PaymentExternalDetailsSchema
>;
export type GetPaymentInput = z.infer<typeof GetPaymentInputSchema>;
export type CreatePaymentInput = z.infer<typeof CreatePaymentInputSchema>;
export type ListPaymentsInput = z.infer<typeof ListPaymentsInputSchema>;
export type ListPaymentsResponse = z.infer<typeof ListPaymentsResponseSchema>;
export type UpdatePaymentInput = z.infer<typeof UpdatePaymentInputSchema>;
export type UpdatePaymentResponse = z.infer<typeof UpdatePaymentResponseSchema>;
export type ApprovePaymentInput = z.infer<typeof ApprovePaymentInputSchema>;
export type ApprovePaymentResponse = z.infer<
  typeof ApprovePaymentResponseSchema
>;

export type ApprovePaymentsInput = z.infer<typeof ApprovePaymentsInputSchema>;
export type ApprovePaymentsResponse = z.infer<
  typeof ApprovePaymentsResponseSchema
>;

export type PaymentDiscrepancy = z.infer<typeof PaymentDiscrepancySchema>;
export type PaymentReviewResult = z.infer<typeof PaymentReviewResultSchema>;
export type ReviewPaymentsInput = z.infer<typeof ReviewPaymentsInputSchema>;
export type ReviewPaymentsResponse = z.infer<
  typeof ReviewPaymentsResponseSchema
>;

export type RequestApprovalEmailInput = z.infer<
  typeof RequestApprovalEmailInputSchema
>;
export type RequestApprovalEmailResponse = z.infer<
  typeof RequestApprovalEmailResponseSchema
>;
export type ListPaymentApprovalsInput = z.infer<
  typeof ListPaymentApprovalsInputSchema
>;
export type ListPaymentApprovalsResponse = z.infer<
  typeof ListPaymentApprovalsResponseSchema
>;
export type PaymentApprovalEntry = z.infer<typeof PaymentApprovalEntrySchema>;
export type PreviewPaymentInput = z.infer<typeof PreviewPaymentInputSchema>;
export type PreviewPaymentResponse = z.infer<
  typeof PreviewPaymentResponseSchema
>;
export type VoidPaymentInput = z.infer<typeof VoidPaymentInputSchema>;
export type VoidPaymentResponse = z.infer<typeof VoidPaymentResponseSchema>;
export type ExecutePaymentInput = z.infer<typeof ExecutePaymentInputSchema>;
export type ExecutePaymentResponse = z.infer<
  typeof ExecutePaymentResponseSchema
>;
export type RecordExternalPaymentInput = z.infer<
  typeof RecordExternalPaymentInputSchema
>;
export type RecordExternalPaymentResponse = z.infer<
  typeof RecordExternalPaymentResponseSchema
>;
export type SimulatePaymentProviderStatusInput = z.infer<
  typeof SimulatePaymentProviderStatusInputSchema
>;
export type SimulatePaymentProviderStatusResponse = z.infer<
  typeof SimulatePaymentProviderStatusResponseSchema
>;

export type PreviewRemittanceEmailInput = z.infer<
  typeof PreviewRemittanceEmailInputSchema
>;
export type PreviewRemittanceEmailResponse = z.infer<
  typeof PreviewRemittanceEmailResponseSchema
>;

export type SendRemittanceEmailInput = z.infer<
  typeof SendRemittanceEmailInputSchema
>;
export type SendRemittanceEmailResponse = z.infer<
  typeof SendRemittanceEmailResponseSchema
>;

export type ListRemittanceEmailsInput = z.infer<
  typeof ListRemittanceEmailsInputSchema
>;
export type ListRemittanceEmailsResponse = z.infer<
  typeof ListRemittanceEmailsResponseSchema
>;
export type RemittanceEmailSummary = z.infer<
  typeof RemittanceEmailSummarySchema
>;

export type GetApprovalRecipientsInput = z.infer<
  typeof GetApprovalRecipientsInputSchema
>;
export type GetApprovalRecipientsResponse = z.infer<
  typeof GetApprovalRecipientsResponseSchema
>;
export type ApprovalRecipient = z.infer<typeof ApprovalRecipientSchema>;
export type PreviewApprovalRequestEmailInput = z.infer<
  typeof PreviewApprovalRequestEmailInputSchema
>;
export type PreviewApprovalRequestEmailResponse = z.infer<
  typeof PreviewApprovalRequestEmailResponseSchema
>;
export type RemoveInvoiceFromPaymentInput = z.infer<
  typeof RemoveInvoiceFromPaymentInputSchema
>;
export type RescheduleInvoiceInput = z.infer<
  typeof RescheduleInvoiceInputSchema
>;
export type RescheduleInvoiceResponse = z.infer<
  typeof RescheduleInvoiceResponseSchema
>;

export type RunAiReviewInput = z.infer<typeof RunAiReviewInputSchema>;
export type RunAiReviewResponse = z.infer<typeof RunAiReviewResponseSchema>;
export type GetAiReviewInput = z.infer<typeof GetAiReviewInputSchema>;
export type AiReviewStatus = z.infer<typeof AiReviewStatusSchema>;
export type AiReviewSummary = z.infer<typeof AiReviewSummarySchema>;
export type RevokeApprovalRequestInput = z.infer<
  typeof RevokeApprovalRequestInputSchema
>;
export type RevokeApprovalRequestResponse = z.infer<
  typeof RevokeApprovalRequestResponseSchema
>;
export type BulkRevokeApprovalRequestInput = z.infer<
  typeof BulkRevokeApprovalRequestInputSchema
>;
export type BulkRevokeApprovalRequestResponse = z.infer<
  typeof BulkRevokeApprovalRequestResponseSchema
>;
